import { memo, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { useBIMStore } from '../../state/useBIMStore';
import { useAppStore } from '../../state/store';
import { useShallow } from 'zustand/react/shallow';
import { BIMElement, WallElement } from '../../core/types';
import { ensureGeometryBVH } from './instancingUtils';
import { dispatchEntityClick } from '../toolEventBus';

/**
 * BIM Category Instancing (plan task 3.1, BIM side)
 *
 * Renders repetitive kernel categories (Walls / Doors / Windows) with a single
 * InstancedMesh per logical part (wall body, door frame/panel, window
 * frame/glass) instead of one mesh per element.
 *
 * Active only in 3D views with shaded-family view modes: plan views rely on
 * per-element 2D symbols, and hiddenLine/wireframe rely on per-element <Edges>.
 * Selection highlighting is handled per-instance via instanceColor.
 *
 * Transform math mirrors the individual renderers in `BIMSceneContent.tsx`
 * exactly (wall body box, door frame/panel, window frame/glass).
 */

export const BIM_INSTANCE_THRESHOLD = 8;

/** View modes eligible for instancing (3D only). */
export const BIM_INSTANCING_VIEW_MODES = new Set(['shaded', 'rendered', 'realistic', 'xray']);

export type BIMInstanceType = 'wall' | 'door' | 'window';
export type BIMInstanceVariant = 'body' | 'frame' | 'panel' | 'glass';

const BIM_INSTANCE_VARIANTS: Record<BIMInstanceType, BIMInstanceVariant[]> = {
    wall: ['body'],
    door: ['frame', 'panel'],
    window: ['frame', 'glass'],
};

// Base colors mirror the individual renderers' shaded-mode colors.
const VARIANT_BASE_COLORS: Record<BIMInstanceVariant, string> = {
    body: '#e0e0e0',
    frame: '#4a4a4a',
    panel: '#8b4513',
    glass: '#87ceeb',
};

const BIM_INSTANCE_SELECTED_COLOR = '#0078d4';

export interface BIMInstanceGroup {
    key: string;
    instanceType: BIMInstanceType;
    /** Host walls this group's placements depend on (door/window groups). */
    hostWallIds: string[];
    /** Element ids in instance order (instanceId -> element id). */
    ids: string[];
    elementsById: Map<string, BIMElement>;
}

export type BIMInstanceGroupCache = Map<string, BIMInstanceGroup>;

export interface BIMInstanceGroupsResult {
    groups: BIMInstanceGroup[];
    /** Ids drawn by instances; the layer renders the rest individually. */
    byElementId: Set<string>;
    cache: BIMInstanceGroupCache;
}

function getBIMInstanceType(el: BIMElement, excludedWallIds: Set<string>): BIMInstanceType | null {
    switch (el.category) {
        case 'Walls': {
            if (excludedWallIds.has(el.id)) return null;
            const g = (el as any).geometryData;
            // Joined/profiled and layered walls have unique geometry (extrusion
            // + CSG), so they stay on the individual renderer path.
            if (!g || !g.start || !g.end) return null;
            if ((g.layers?.length ?? 0) > 0) return null;
            if ((g.profile?.length ?? 0) > 0) return null;
            if ((g.layerProfiles?.length ?? 0) > 0) return null;
            return 'wall';
        }
        case 'Doors':
        case 'Windows': {
            const g = (el as any).geometryData;
            if (!g || !g.width || !g.height) return null;
            return el.category === 'Doors' ? 'door' : 'window';
        }
        default:
            return null;
    }
}

/**
 * Partitions elements into instance groups keyed by (type, host wall).
 *
 * The cache keeps group object identities stable while membership and element
 * identities are unchanged, so instanced batches skip matrix rewrites on
 * unrelated commands. Untouched elements stay referentially stable thanks to
 * the kernel's replace-on-write semantics (task 2.2).
 */
export function buildBIMInstanceGroups(
    elementsMap: Map<string, BIMElement>,
    threshold: number,
    excludedWallIds: Set<string>,
    previousCache: BIMInstanceGroupCache
): BIMInstanceGroupsResult {
    const buckets = new Map<string, {
        instanceType: BIMInstanceType;
        hostWallIds: string[];
        ids: string[];
        elementsById: Map<string, BIMElement>;
    }>();

    elementsMap.forEach((el) => {
        const instanceType = getBIMInstanceType(el, excludedWallIds);
        if (!instanceType) return;
        const hostWallId = (el as any).geometryData?.hostWallId as string | undefined;
        const key = `${instanceType}|${hostWallId ?? ''}`;
        let bucket = buckets.get(key);
        if (!bucket) {
            bucket = {
                instanceType,
                hostWallIds: hostWallId ? [hostWallId] : [],
                ids: [],
                elementsById: new Map<string, BIMElement>(),
            };
            buckets.set(key, bucket);
        }
        bucket.ids.push(el.id);
        bucket.elementsById.set(el.id, el);
    });

    const nextCache: BIMInstanceGroupCache = new Map();
    const groups: BIMInstanceGroup[] = [];
    const byElementId = new Set<string>();

    buckets.forEach((bucket, key) => {
        if (bucket.ids.length < threshold) return;
        const cached = previousCache.get(key);
        const isStable =
            cached !== undefined &&
            cached.ids.length === bucket.ids.length &&
            cached.ids.every((id, _index) => cached.elementsById.get(id) === bucket.elementsById.get(id));
        const group: BIMInstanceGroup = isStable && cached
            ? cached
            : {
                key,
                instanceType: bucket.instanceType,
                hostWallIds: bucket.hostWallIds,
                ids: bucket.ids,
                elementsById: bucket.elementsById,
            };
        nextCache.set(key, group);
        groups.push(group);
        bucket.ids.forEach((id) => byElementId.add(id));
    });

    return { groups, byElementId, cache: nextCache };
}

// Reusable scratch objects (single-threaded render-phase usage, same pattern
// as InstancedEntityGroup).
const _matrix = new THREE.Matrix4();
const _position = new THREE.Vector3();
const _quaternion = new THREE.Quaternion();
const _euler = new THREE.Euler();
const _scale = new THREE.Vector3();
const _pointA = new THREE.Vector3();
const _pointB = new THREE.Vector3();

/**
 * Composes an instance matrix mirroring the individual renderers:
 * - Wall: unit box scaled to (length, thickness, height), rotated around Z,
 *   centered at wall midpoint at baseOffset + height / 2.
 * - Door: frame/panel boxes centered at lerp position + baseOffset + height/2.
 * - Window: frame/glass boxes centered at lerp position + baseOffset +
 *   sillHeight + height/2.
 */
export function composeBIMInstanceMatrix(
    instanceType: BIMInstanceType,
    variant: BIMInstanceVariant,
    element: BIMElement,
    hostWall: WallElement | undefined,
    target: THREE.Matrix4
): void {
    const g = (element as any).geometryData || {};

    if (instanceType === 'wall') {
        const height = g.height ?? 3;
        const thickness = g.thickness ?? 0.2;
        const baseZ = g.baseOffset || 0;
        _pointA.set(g.start[0], g.start[1], g.start[2]);
        _pointB.set(g.end[0], g.end[1], g.end[2]);
        const length = _pointA.distanceTo(_pointB);
        _position.copy(_pointA).add(_pointB).multiplyScalar(0.5);
        _position.z = baseZ + height / 2;
        _euler.set(0, 0, Math.atan2(_pointB.y - _pointA.y, _pointB.x - _pointA.x));
        _quaternion.setFromEuler(_euler);
        _scale.set(length, thickness, height);
    } else {
        const isDoor = instanceType === 'door';
        const width = g.width || (isDoor ? 0.915 : 1.2);
        const height = g.height || (isDoor ? 2.134 : 1.5);
        const frameWidth = 0.05;
        const sillHeight = g.sillHeight || 0.9;
        const baseZ = hostWall?.geometryData?.baseOffset || 0;
        const wallThickness = hostWall?.geometryData?.thickness || 0.2;

        if (hostWall) {
            _pointA.set(...hostWall.geometryData.start);
            _pointB.set(...hostWall.geometryData.end);
            _position.copy(_pointA).lerp(_pointB, g.position || 0);
            _euler.set(0, 0, Math.atan2(_pointB.y - _pointA.y, _pointB.x - _pointA.x));
        } else {
            _position.set(0, 0, 0);
            _euler.set(0, 0, 0);
        }
        _quaternion.setFromEuler(_euler);

        _position.z += baseZ + (isDoor ? 0 : sillHeight) + height / 2;

        if (variant === 'frame') {
            _scale.set(width + frameWidth * 2, wallThickness + 0.02, height);
        } else if (variant === 'panel') {
            _scale.set(width, 0.04, height - 0.02);
        } else {
            _scale.set(width - frameWidth * 2, 0.02, height - frameWidth * 2);
        }
    }

    target.compose(_position, _quaternion, _scale);
}

const EMPTY_HOST_WALLS: WallElement[] = [];

interface BIMInstanceBatchProps {
    group: BIMInstanceGroup;
    variant: BIMInstanceVariant;
    wallById: Map<string, WallElement>;
    selectedIdSet: Set<string>;
    /** Unified cross-workbench picking: attach the entity-click dispatch. */
    interactive?: boolean;
}

const BIMInstanceBatch = memo(function BIMInstanceBatch({ group, variant, wallById, selectedIdSet, interactive = false }: BIMInstanceBatchProps) {
    const meshRef = useRef<THREE.InstancedMesh>(null);
    const geometryRef = useRef<THREE.BoxGeometry>(null);

    const baseColor = useMemo(() => new THREE.Color(VARIANT_BASE_COLORS[variant]), [variant]);
    const selectedColor = useMemo(() => new THREE.Color(BIM_INSTANCE_SELECTED_COLOR), []);

    // Grow-only capacity buffer (was `key={ids.length}` + exact-capacity args,
    // which remounted — dispose, GPU re-upload, BVH rebuild — on EVERY member
    // add/remove). Capacity rounds up to 32 so a batch reallocates at most
    // once per 32 membership changes; R3F rebuilds only when `args` change.
    const capacityRef = useRef(0);
    const needed = Math.max(1, group.ids.length);
    if (needed > capacityRef.current) {
        capacityRef.current = Math.ceil(needed / 32) * 32;
    }
    const capacity = capacityRef.current;

    // Write instance matrices/colors whenever inputs change (task 3.1)
    useLayoutEffect(() => {
        const mesh = meshRef.current;
        if (!mesh) return;

        group.ids.forEach((id, index) => {
            const element = group.elementsById.get(id);
            if (!element) return;
            const hostWall = wallById.get((element as any).geometryData?.hostWallId);
            composeBIMInstanceMatrix(group.instanceType, variant, element, hostWall, _matrix);
            mesh.setMatrixAt(index, _matrix);
            mesh.setColorAt(index, selectedIdSet.has(id) ? selectedColor : baseColor);
        });
        // Pre-size instanceColor for the full capacity: three's lazy
        // setColorAt allocates only `count` entries, which would underflow
        // when mesh.count grows later without a remount.
        if (!mesh.instanceColor || mesh.instanceColor.count < capacity) {
            mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3).fill(1), 3);
        }
        mesh.count = group.ids.length;
        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
        mesh.computeBoundingSphere();
    }, [group, variant, wallById, selectedIdSet, baseColor, selectedColor, capacity]);

    // BVH-accelerated raycasting on the shared unit geometry (task 3.4 parity)
    useLayoutEffect(() => {
        if (geometryRef.current) ensureGeometryBVH(geometryRef.current);
    }, []);

    // Unified picking (cross-workbench layers only): resolve the clicked
    // instance back to its element id and route it through the shared
    // entity-click router, same contract as the individual renderers.
    const handleInstanceClick = useCallback((e: ThreeEvent<MouseEvent>) => {
        if (e.instanceId == null) return;
        const elementId = group.ids[e.instanceId];
        if (!elementId) return;
        e.stopPropagation();
        if (useAppStore.getState().activeTool !== 'select') return;
        dispatchEntityClick(elementId, e);
    }, [group]);

    return (
        <instancedMesh
            ref={meshRef}
            args={[undefined, undefined, capacity]}
            userData={{ bimInstanceIds: group.ids, bimInstanceType: group.instanceType }}
            onClick={interactive ? handleInstanceClick : undefined}
        >
            <boxGeometry ref={geometryRef} args={[1, 1, 1]} />
            <meshStandardMaterial color="#ffffff" roughness={0.7} metalness={0.1} />
        </instancedMesh>
    );
});

/**
 * One instanced category group: renders its variants (door frame + panel,
 * window frame + glass, wall body) as InstancedMeshes with per-instance
 * selection highlighting. Matrices refresh when a host wall geometry changes.
 */
export const BIMInstancedCategory = memo(function BIMInstancedCategory({ group, interactive = false }: { group: BIMInstanceGroup; interactive?: boolean }) {
    const bimSelection = useBIMStore((state) => state.selection);
    const appSelection = useAppStore((state) => state.selectedIds);
    const selectedIdSet = useMemo(() => new Set([...bimSelection, ...appSelection]), [bimSelection, appSelection]);

    const hostWallIds = group.hostWallIds;
    // Set lookup instead of `hostWallIds.includes` per element: the selector
    // runs for every store write, so the per-element scan cost matters.
    const hostWallIdSet = useMemo(() => new Set(hostWallIds), [hostWallIds]);
    // Subscribe to the host walls of this group so door/window placements
    // refresh when a host wall geometry is edited (replace-on-write identity).
    const hostWalls = useBIMStore(useShallow((state) => {
        if (hostWallIdSet.size === 0) return EMPTY_HOST_WALLS;
        const out: WallElement[] = [];
        state.project.elements.forEach((el) => {
            if (hostWallIdSet.has(el.id)) out.push(el as WallElement);
        });
        return out;
    }));

    const wallById = useMemo(() => {
        const map = new Map<string, WallElement>();
        hostWalls.forEach((wall) => map.set(wall.id, wall));
        return map;
    }, [hostWalls]);

    return (
        <group>
            {BIM_INSTANCE_VARIANTS[group.instanceType].map((variant) => (
                <BIMInstanceBatch
                    key={variant}
                    group={group}
                    variant={variant}
                    wallById={wallById}
                    selectedIdSet={selectedIdSet}
                    interactive={interactive}
                />
            ))}
        </group>
    );
});